import { SlidersHorizontal, Table2 } from 'lucide-react';
import { formatOrdinalDateTimeInTimeZone } from '../../../../utils/dateFormat';
import type { ReportResult, ReportTable } from '../../../../types/reports';
import { describeFilters } from '../reportFormat';
import { BreakdownCard } from './BreakdownCard';
import { BreakdownTable } from './BreakdownTable';
import { DetailsTable } from './DetailsTable';
import { SummaryCard } from './SummaryCard';

/** Generated-report view: metadata, applied filters, KPI cards and detail table. */
export function ReportResults({ result, groupBy }: { result: ReportResult; groupBy: string }) {
  const activeFilters = describeFilters(result.filters);
  // The category breakdown gets a full-width table (every category), while the
  // remaining breakdowns render as compact summary cards.
  const categoryBreakdown =
    result.breakdowns.find((breakdown) => breakdown.key === 'category' && breakdown.items.length > 0) ?? null;
  const summaryBreakdowns = result.breakdowns.filter(
    (breakdown) => breakdown.key !== 'category' && breakdown.items.length > 0,
  );
  // A `top` table is a summary that belongs above the ledger (the IPD report's
  // per-Service-Type cut); everything else follows the details as before.
  const topTables = (result.tables ?? []).filter((table) => table.position === 'top');
  const bottomTables = (result.tables ?? []).filter((table) => table.position !== 'top');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-surface px-4 py-2.5 shadow-sm">
        <p className="flex items-center gap-2 text-xs text-text-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          Generated {formatOrdinalDateTimeInTimeZone(result.generatedAt, result.timezone)}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-primary">
            {result.total.toLocaleString('en-US')} record{result.total === 1 ? '' : 's'}
          </span>
          {result.truncated && (
            <span className="rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning">
              First {result.returned.toLocaleString('en-US')} shown
            </span>
          )}
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="flex items-center gap-1 text-[11px] font-medium text-text-muted">
            <SlidersHorizontal size={12} />
            Applied
          </span>
          {activeFilters.map((filter) => (
            <span
              key={filter}
              className="rounded-full border border-border bg-surface px-2 py-0.5 text-[11px] text-text-muted"
            >
              {filter}
            </span>
          ))}
        </div>
      )}

      {result.summary.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
          {result.summary.map((item) => (
            <SummaryCard key={item.label} item={item} />
          ))}
        </div>
      )}

      {categoryBreakdown && <BreakdownTable breakdown={categoryBreakdown} />}

      {summaryBreakdowns.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {summaryBreakdowns.map((breakdown) => (
            <BreakdownCard key={breakdown.title} breakdown={breakdown} />
          ))}
        </div>
      )}

      {topTables.map((table) => (
        <TableBlock key={table.title} table={table} timezone={result.timezone} />
      ))}

      <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
            <Table2 size={15} className="text-primary" />
            {result.reportId === 'revenue-summary'
              ? groupBy === 'type'
                ? 'Revenue by Service Type'
                : 'IPD Patient Bills — Invoices'
              : result.reportId === 'diagnostic-revenue'
                ? groupBy === 'diagnostic-category'
                  ? 'Revenue by Diagnostic Category'
                  : 'OPD Diagnostic Receipts'
                : result.reportId === 'income-summary'
                  ? groupBy === 'source'
                    ? 'Income by Source'
                    : groupBy === 'method'
                      ? 'Cash Collected by Payment Method'
                      : groupBy === 'daily'
                        ? 'Revenue and Cash by Date'
                        : 'Income Documents'
                  : groupBy === 'detail'
                    ? 'All Expenses'
                    : groupBy
                      ? 'Grouped amounts'
                      : 'Details'}
          </h3>
          <span className="text-[11px] text-text-muted">
            {result.rows.length.toLocaleString('en-US')} row
            {result.rows.length === 1 ? '' : 's'}
          </span>
        </div>
        <DetailsTable columns={result.columns} rows={result.rows} timezone={result.timezone} />
      </div>

      {bottomTables.map((table) => (
        <TableBlock key={table.title} table={table} timezone={result.timezone} />
      ))}
    </div>
  );
}

function TableBlock({
  table,
  timezone,
}: {
  table: ReportTable;
  timezone?: string;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
          <Table2 size={15} className="text-primary" />
          {table.title}
        </h3>
        <span className="text-[11px] text-text-muted">
          {table.rows.length.toLocaleString('en-US')} row
          {table.rows.length === 1 ? '' : 's'}
        </span>
      </div>
      <DetailsTable columns={table.columns} rows={table.rows} timezone={timezone} />
    </div>
  );
}
